// Per-unit record-of-deed owners. Mirrors ExternalOwnersPanel but
// scoped to a unit, since the tulajdoni lap (and therefore the list of
// owners on it) describes one ingatlan. Mounted under each unit card by
// PropertyView.

import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { icon } from '../core/icons.js';
import { t } from '../core/i18n.js';
import { countryFieldHtml, addressPasteFieldHtml, wireAddressPaste } from './PropertyBasicsFields.js';

export class UnitExternalOwnersPanel extends Component {
  constructor({
    unitId, canManage,
    propertyId = null, landlordExternalOwnerId = null, canDesignateIssuer = false, onIssuerChange = null,
  }) {
    super();
    this.unitId = unitId;
    this.canManage = !!canManage;
    // Billing-issuer designation (properties.landlord_external_owner_id).
    // Only wired when the host passes propertyId; the wizard and the
    // property page do, standalone unit contexts don't.
    this.propertyId = propertyId || null;
    this.canDesignateIssuer = !!canDesignateIssuer;
    this._onIssuerChange = typeof onIssuerChange === 'function' ? onIssuerChange : null;
    this.state = {
      issuerId: landlordExternalOwnerId || null,
      owners: null,
      error: null,
      addOpen: false,
      detailsOpen: false,
      panelOpen: true,        // <details open> state for the section fold
      busy: false,
      // `form` holds every editable field on the add form. The inline
      // top section only renders name + share; the rest live under a
      // "More details" disclosure. Picking an autocomplete suggestion
      // fills the whole `form` in one go so the user doesn't have to
      // open the disclosure to confirm. `_skipDetailRender` is a flag
      // set after an autocomplete pick so we keep the user-edited
      // detail values across re-renders (every input has its own
      // input listener that pushes back into form).
      form: this._blankForm(),
      suggestions: null,
      suggestBusy: false,
      // When set, the form is editing an existing owner (PUT) rather
      // than adding a new one (POST). Holds the owner id.
      editingId: null,
    };
    this._suggestTimer = null;
    this._suggestSeq = 0;
  }

  /**
   * Every editable field is tracked in `form` so submission is one
   * sweep and autocomplete prefill is one assignment. Detail-section
   * fields stay null/'' when untouched so the repository writes NULL
   * rather than empty strings.
   */
  _blankForm() {
    return {
      name: '', share: '',
      entity_type: 'individual',
      email: '', phone: '',
      // Structured address (migration 0080): irányítószám / város /
      // ország reuse the billing_* columns; the street parts are their
      // own fields. billing_address_line1/2 are derived server-side.
      billing_country: 'HU', billing_postal_code: '', billing_city: '',
      addr_street: '', addr_house_number: '', addr_building: '',
      addr_staircase: '', addr_floor: '', addr_door: '', addr_hrsz: '',
      birth_name: '', birth_date: '', birth_place: '', mothers_name: '',
      id_card_number: '', tax_id: '',
      company_reg: '', representative_name: '', representative_role: '',
      bank_account: '',
    };
  }

  /**
   * Build the editable form from an existing owner row. Every column
   * the API returns maps 1:1 into form.*; missing values become ''.
   * Used by the per-row Edit button.
   */
  _formFromOwner(o) {
    const s = (v) => (v == null ? '' : String(v));
    return {
      name: s(o.name), share: s(o.share),
      entity_type: o.entity_type === 'company' ? 'company' : 'individual',
      email: s(o.email), phone: s(o.phone),
      billing_country: s(o.billing_country) || 'HU',
      billing_postal_code: s(o.billing_postal_code),
      billing_city: s(o.billing_city),
      addr_street: s(o.addr_street), addr_house_number: s(o.addr_house_number),
      addr_building: s(o.addr_building), addr_staircase: s(o.addr_staircase),
      addr_floor: s(o.addr_floor), addr_door: s(o.addr_door), addr_hrsz: s(o.addr_hrsz),
      birth_name: s(o.birth_name), birth_date: s(o.birth_date),
      birth_place: s(o.birth_place), mothers_name: s(o.mothers_name),
      id_card_number: s(o.id_card_number), tax_id: s(o.tax_id),
      company_reg: s(o.company_reg), representative_name: s(o.representative_name),
      representative_role: s(o.representative_role),
      bank_account: s(o.bank_account),
    };
  }

  template() {
    const rows = this.state.owners;
    const count = Array.isArray(rows) ? rows.length : 0;
    const title = `${t('unit.external_owners.title')} · ${count}`;
    const meta = String(t('unit.external_owners.section_meta') || t('unit.external_owners.subtitle') || '').toUpperCase();
    const open = this.state.panelOpen !== false;
    const addLabel = `+ ${String(t('unit.external_owners.add') || 'Add owner').toUpperCase()}`;
    return `
      <details class="unit-section-fold" data-unit-external-owners-panel${open ? ' open' : ''}>
        <summary>
          <span>${esc(title)}</span>
          <span class="unit-section-fold-meta">${esc(meta)}</span>
        </summary>
        <div class="unit-section-fold-body">
          ${this.state.error ? `<div class="error-banner">${esc(this.state.error)}</div>` : ''}
          ${rows === null
            ? `<div class="muted">${esc(t('unit.external_owners.loading'))}</div>`
            : count === 0
              ? `<div class="empty" style="background: var(--color-bg-card); border: 1px dashed var(--color-border); border-radius: 8px; padding: 16px; text-align: center; color: var(--color-text-faint);">${esc(t('unit.external_owners.empty'))}</div>`
              : `<div class="list">${rows.map((o) => this._rowHtml(o)).join('')}</div>`}
          ${this.canManage && !this.state.editingId
            ? (this.state.addOpen
                ? this._formHtml()
                : `<button type="button" class="fold-add-trigger" data-action="add-open"><span class="fold-add-trigger-summary">${esc(addLabel)}</span></button>`)
            : ''}
        </div>
      </details>
    `;
  }

  _rowHtml(o) {
    // When this row is the one being edited, render the form inline in
    // its place instead of the static row.
    if (this.state.editingId && this.state.editingId === o.id) {
      return this._formHtml();
    }
    const addr = o.billing_address_line1
      ? [o.billing_postal_code, o.billing_city, o.billing_address_line1].filter(Boolean).join(' ')
      : (o.address || '');
    const meta = [
      o.share || '',
      addr,
      o.source === 'ocr' ? t('unit.external_owners.source.ocr') : t('unit.external_owners.source.manual'),
    ].filter(Boolean).join(' · ');
    return `
      <div class="list-item">
        ${icon('user', { class: 'list-leading icon-faint' })}
        <div class="stack-2 grow">
          <strong>${esc(o.name || '—')}</strong>
          <span class="faint">${esc(meta)}</span>
        </div>
        ${this._issuerToggleHtml(o)}
        ${this.canManage ? `
          <button class="btn btn-ghost btn-icon" data-action="edit" data-id="${esc(o.id)}"
                  aria-label="${esc(t('common.edit'))}">${icon('pencil', { size: 'sm' })}</button>
          <button class="btn btn-ghost btn-icon" data-action="remove" data-id="${esc(o.id)}"
                  aria-label="${esc(t('unit.external_owners.remove'))}">${icon('trash', { size: 'sm' })}</button>` : ''}
      </div>
    `;
  }

  /**
   * Billing-issuer radio for an owner row. Mirrors MembersPanel's
   * landlord-of-record toggle: a member user and an external owner are
   * mutually exclusive issuers (server clears one when setting the
   * other). Radios share one name across every unit panel on the page
   * so at most one deed owner reads as selected.
   */
  _issuerToggleHtml(o) {
    if (!this.propertyId) return '';
    const isIssuer = this.state.issuerId && String(o.id) === String(this.state.issuerId);
    if (!this.canDesignateIssuer) {
      return isIssuer
        ? `<span class="badge badge-accent" title="${esc(t('unit.external_owners.issuer_hint'))}">${esc(t('unit.external_owners.issuer'))}</span>`
        : '';
    }
    return `
      <label class="row" style="gap:var(--space-2); align-items:center; margin-right:var(--space-2)">
        <input type="radio" name="property-issuer-ext" data-action="set-issuer"
          data-id="${esc(o.id)}" ${isIssuer ? 'checked' : ''}
          aria-label="${esc(t('unit.external_owners.issuer_aria'))}" />
        <span class="faint">${esc(t('unit.external_owners.issuer'))}</span>
      </label>
    `;
  }

  _formHtml() {
    const f = this.state.form;
    const sug = this.state.suggestions;
    const detailsOpen = this.state.detailsOpen;
    const isEdit = !!this.state.editingId;
    const heading = isEdit
      ? String(t('unit.external_owners.edit') || 'Edit owner').toUpperCase()
      : `+ ${String(t('unit.external_owners.add') || 'Add owner').toUpperCase()}`;
    return `
      <form class="fold-add-trigger open" data-external-owner-form>
        <div class="fold-add-trigger-summary">${esc(heading)}</div>
        <div class="row" style="gap:var(--space-3); flex-wrap:wrap; align-items:flex-start">
          <div class="field grow" style="position:relative; min-width:200px">
            <label>${esc(t('unit.external_owners.form.name'))}</label>
            <input class="input" name="name" required value="${esc(f.name)}" autocomplete="off" data-role="owner-name-search" />
            ${this._suggestionsHtml(sug)}
          </div>
          <div class="field" style="flex:1 1 140px; min-width:120px"><label>${esc(t('unit.external_owners.form.share'))}</label>
            <input class="input" name="share" value="${esc(f.share)}" autocomplete="off" /></div>
        </div>
        <div>
          <button type="button" class="btn btn-ghost btn-sm" data-action="toggle-details">
            ${icon(detailsOpen ? 'chevron-down' : 'chevron-right', { size: 'sm' })}<span>${esc(t('unit.external_owners.form.more_details'))}</span>
          </button>
        </div>
        ${detailsOpen ? this._detailsHtml(f) : ''}
        <div class="row" style="justify-content:flex-end; gap:var(--space-3)">
          <button type="button" class="btn btn-ghost" data-action="add-cancel">${esc(t('common.cancel'))}</button>
          <button type="submit" class="btn btn-primary" ${this.state.busy ? 'disabled' : ''}>
            ${esc(t('unit.external_owners.form.submit'))}
          </button>
        </div>
      </form>
    `;
  }

  /**
   * Contract-grade detail fields. Entity-type radio drives whether the
   * company-only sub-form (cégjegyzékszám, képviselő) is rendered. The
   * tax-ID label switches between adóazonosító jel and adószám.
   */
  _detailsHtml(f) {
    const isCompany = f.entity_type === 'company';
    const taxLabel = isCompany ? t('unit.external_owners.form.adoszam') : t('unit.external_owners.form.adoazonosito');
    return `
      <div class="stack" style="border-top:1px solid var(--color-border); padding-top:var(--space-2)">
        <fieldset class="stack-2" style="border:none; padding:0; margin:0">
          <legend class="faint" style="padding:0">${esc(t('unit.external_owners.form.entity_type'))}</legend>
          <div class="row" style="gap:var(--space-3)">
            <label class="row" style="gap:var(--space-2); align-items:center">
              <input type="radio" name="entity_type" value="individual" ${!isCompany ? 'checked' : ''} />
              <span>${esc(t('me.billing_entity_individual'))}</span>
            </label>
            <label class="row" style="gap:var(--space-2); align-items:center">
              <input type="radio" name="entity_type" value="company" ${isCompany ? 'checked' : ''} />
              <span>${esc(t('me.billing_entity_company'))}</span>
            </label>
          </div>
        </fieldset>

        <div class="row" style="gap:var(--space-3); flex-wrap:wrap; align-items:flex-start">
          <div class="field" style="flex:1 1 240px"><label>${esc(t('unit.external_owners.form.email'))}</label>
            <input class="input" type="email" name="email" value="${esc(f.email)}" /></div>
          <div class="field" style="flex:1 1 160px; min-width:140px"><label>${esc(t('unit.external_owners.form.phone'))}</label>
            <input class="input" name="phone" value="${esc(f.phone)}" /></div>
        </div>

        <strong class="faint" style="font-size:0.9em">${esc(t('unit.external_owners.form.address_section'))}</strong>
        ${addressPasteFieldHtml()}
        <div class="row" style="gap:var(--space-3); flex-wrap:wrap; align-items:flex-start">
          <div class="field" style="flex:1 1 120px; min-width:110px"><label>${esc(t('unit.external_owners.form.addr_postal') || t('me.billing_postal_code'))}</label>
            <input class="input" name="billing_postal_code" maxlength="32" value="${esc(f.billing_postal_code)}" /></div>
          <div class="field" style="flex:2 1 180px; min-width:140px"><label>${esc(t('unit.external_owners.form.addr_city') || t('me.billing_city'))}</label>
            <input class="input" name="billing_city" maxlength="128" value="${esc(f.billing_city)}" /></div>
          <div class="field" style="flex:1 1 160px; min-width:140px"><label>${esc(t('unit.external_owners.form.addr_country') || t('me.billing_country'))}</label>
            ${countryFieldHtml(f.billing_country, { name: 'billing_country' })}</div>
        </div>
        <div class="row" style="gap:var(--space-3); flex-wrap:wrap; align-items:flex-start">
          <div class="field" style="flex:2 1 200px; min-width:160px"><label>${esc(t('unit.external_owners.form.addr_street') || 'Utca, közterület')}</label>
            <input class="input" name="addr_street" maxlength="190" placeholder="Andrássy út" value="${esc(f.addr_street)}" /></div>
          <div class="field" style="flex:1 1 100px; min-width:90px"><label>${esc(t('unit.external_owners.form.addr_house_number') || 'Házszám')}</label>
            <input class="input" name="addr_house_number" maxlength="32" placeholder="12" value="${esc(f.addr_house_number)}" /></div>
        </div>
        <div class="row" style="gap:var(--space-3); flex-wrap:wrap; align-items:flex-start">
          <div class="field" style="flex:1 1 90px; min-width:80px"><label>${esc(t('unit.external_owners.form.addr_building') || 'Épület')}</label>
            <input class="input" name="addr_building" maxlength="32" placeholder="B" value="${esc(f.addr_building)}" /></div>
          <div class="field" style="flex:1 1 90px; min-width:80px"><label>${esc(t('unit.external_owners.form.addr_staircase') || 'Lépcsőház')}</label>
            <input class="input" name="addr_staircase" maxlength="32" value="${esc(f.addr_staircase)}" /></div>
          <div class="field" style="flex:1 1 90px; min-width:80px"><label>${esc(t('unit.external_owners.form.addr_floor') || 'Emelet')}</label>
            <input class="input" name="addr_floor" maxlength="32" placeholder="3" value="${esc(f.addr_floor)}" /></div>
          <div class="field" style="flex:1 1 90px; min-width:80px"><label>${esc(t('unit.external_owners.form.addr_door') || 'Ajtó')}</label>
            <input class="input" name="addr_door" maxlength="32" placeholder="5" value="${esc(f.addr_door)}" /></div>
        </div>
        <div class="field"><label>${esc(t('unit.external_owners.form.addr_hrsz') || 'Helyrajzi szám')} <span class="faint" style="text-transform:none; letter-spacing:0; font-size:12px;">(${esc(t('common.optional') || 'opcionális')})</span></label>
          <input class="input" name="addr_hrsz" maxlength="64" placeholder="29732/0/A/12" value="${esc(f.addr_hrsz)}" /></div>

        ${!isCompany ? `
          <strong class="faint" style="font-size:0.9em">${esc(t('unit.external_owners.form.identity_section'))}</strong>
          <div class="row" style="gap:var(--space-3); flex-wrap:wrap; align-items:flex-start">
            <div class="field" style="flex:1 1 220px"><label>${esc(t('unit.external_owners.form.birth_name'))}</label>
              <input class="input" name="birth_name" value="${esc(f.birth_name)}" /></div>
            <div class="field" style="flex:1 1 220px"><label>${esc(t('unit.external_owners.form.mothers_name'))}</label>
              <input class="input" name="mothers_name" value="${esc(f.mothers_name)}" /></div>
          </div>
          <div class="row" style="gap:var(--space-3); flex-wrap:wrap; align-items:flex-start">
            <div class="field" style="flex:1 1 180px"><label>${esc(t('unit.external_owners.form.birth_date'))}</label>
              <input class="input" type="date" name="birth_date" value="${esc(f.birth_date)}" /></div>
            <div class="field" style="flex:1 1 220px"><label>${esc(t('unit.external_owners.form.birth_place'))}</label>
              <input class="input" name="birth_place" value="${esc(f.birth_place)}" /></div>
          </div>
          <div class="row" style="gap:var(--space-3); flex-wrap:wrap; align-items:flex-start">
            <div class="field" style="flex:1 1 200px"><label>${esc(t('unit.external_owners.form.id_card_number'))}</label>
              <input class="input" name="id_card_number" value="${esc(f.id_card_number)}" /></div>
            <div class="field" style="flex:1 1 200px"><label>${esc(taxLabel)}</label>
              <input class="input" name="tax_id" value="${esc(f.tax_id)}" /></div>
          </div>
        ` : `
          <strong class="faint" style="font-size:0.9em">${esc(t('unit.external_owners.form.company_section'))}</strong>
          <div class="row" style="gap:var(--space-3); flex-wrap:wrap; align-items:flex-start">
            <div class="field" style="flex:1 1 200px"><label>${esc(taxLabel)}</label>
              <input class="input" name="tax_id" value="${esc(f.tax_id)}" /></div>
            <div class="field" style="flex:1 1 200px"><label>${esc(t('unit.external_owners.form.company_reg'))}</label>
              <input class="input" name="company_reg" value="${esc(f.company_reg)}" /></div>
          </div>
          <div class="row" style="gap:var(--space-3); flex-wrap:wrap; align-items:flex-start">
            <div class="field" style="flex:2 1 240px"><label>${esc(t('unit.external_owners.form.representative_name'))}</label>
              <input class="input" name="representative_name" value="${esc(f.representative_name)}" /></div>
            <div class="field" style="flex:1 1 180px"><label>${esc(t('unit.external_owners.form.representative_role'))}</label>
              <input class="input" name="representative_role" value="${esc(f.representative_role)}" /></div>
          </div>
        `}

        <div class="field"><label>${esc(t('unit.external_owners.form.bank_account'))}</label>
          <input class="input" name="bank_account" maxlength="64" placeholder="12345678-12345678-12345678 / IBAN" value="${esc(f.bank_account)}" /></div>
      </div>
    `;
  }

  /**
   * Type-ahead dropdown rendered under the name input. Empty array =
   * "no matches"; null = "haven't searched yet" (don't render). Each
   * row is a pickable button; clicking pre-fills the whole owner
   * payload (address, birth_*, mothers_name, jogcim, ownership_title,
   * share) into state.prefill so the deeper fields carry on submit
   * without bloating the visible form.
   */
  _suggestionsHtml(sug) {
    if (sug === null) return '';
    if (this.state.suggestBusy) {
      return `<div class="suggest-dropdown muted">${esc(t('common.searching'))}</div>`;
    }
    if (sug.length === 0) {
      return `<div class="suggest-dropdown faint">${esc(t('unit.external_owners.suggest.empty'))}</div>`;
    }
    return `
      <div class="suggest-dropdown list">
        ${sug.map((r, i) => `
          <button type="button" class="list-item" data-action="pick-suggestion" data-idx="${i}">
            ${icon('user', { class: 'list-leading icon-faint' })}
            <div class="stack-2 grow">
              <strong>${esc(r.name || '—')}</strong>
              <span class="faint">${esc([r.share, r.address, r.birth_date].filter(Boolean).join(' · '))}</span>
            </div>
          </button>
        `).join('')}
      </div>
    `;
  }

  destroy() {
    if (this._suggestTimer) clearTimeout(this._suggestTimer);
  }

  mount() {
    this.on('[data-action="add-open"]', 'click', () => this.setState({ addOpen: !this.state.addOpen, panelOpen: true, error: null }));
    if (this.el && this.el.tagName === 'DETAILS') {
      this.el.addEventListener('toggle', (e) => {
        const isOpen = !!(e.currentTarget && e.currentTarget.open);
        if (isOpen !== (this.state.panelOpen !== false)) {
          this.state.panelOpen = isOpen;
        }
      });
    }
    this.on('[data-action="add-cancel"]', 'click', () => this.setState({
      addOpen: false, detailsOpen: false, error: null, editingId: null,
      form: this._blankForm(),
      suggestions: null,
    }));
    this.on('[data-action="toggle-details"]', 'click', () => this.setState({ detailsOpen: !this.state.detailsOpen }));
    this.on('[data-action="set-issuer"]', 'change', (e) => this._setIssuer(e.currentTarget.getAttribute('data-id')));
    this.on('[data-action="edit"]', 'click', (e) => this._openEdit(e.currentTarget.getAttribute('data-id')));
    this.on('[data-action="remove"]', 'click', (e) => this._remove(e.currentTarget.getAttribute('data-id')));
    this.on('[data-action="pick-suggestion"]', 'click', (e) => this._pickSuggestion(Number(e.currentTarget.getAttribute('data-idx'))));
    const form = this.el.querySelector('[data-external-owner-form]');
    if (form) {
      form.querySelectorAll('input, select').forEach((inp) => {
        const handler = (e) => {
          const name = e.target.name;
          if (!name) return;
          this.state.form[name] = e.target.value;
          // Don't run the type-ahead while editing an existing owner —
          // we already know who they are; the dropdown would just be noise.
          if (name === 'name' && !this.state.editingId) this._scheduleSuggest(e.target.value);
          // entity_type radio toggles which company-only fields render,
          // so we need a re-render. Plain text inputs don't — they
          // already hold their value in the DOM and re-rendering would
          // steal keyboard focus mid-edit.
          if (name === 'entity_type' && inp.type === 'radio' && e.target.checked) {
            this.setState({});
          }
        };
        inp.addEventListener('input', handler);
        if (inp.type === 'radio') inp.addEventListener('change', handler);
      });
      form.addEventListener('submit', (e) => this._submit(e));
      // Smart address paste: fills the billing_*/addr_* fields; the per-input
      // handlers above pick up the dispatched events into state.form.
      wireAddressPaste(form);
    }
    if (this.state.owners === null) this._load();
  }

  _scheduleSuggest(value) {
    if (this._suggestTimer) clearTimeout(this._suggestTimer);
    const q = (value || '').trim();
    if (q.length < 2) {
      if (this.state.suggestions !== null || this.state.suggestBusy) {
        this.setState({ suggestions: null, suggestBusy: false });
      }
      return;
    }
    this.state.suggestBusy = true;
    this.setState({});
    const mySeq = ++this._suggestSeq;
    this._suggestTimer = setTimeout(async () => {
      try {
        const res = await api.get(`/me/external-owners/suggest?q=${encodeURIComponent(q)}`);
        // Stale-response guard: drop results from an older request
        // that arrived after a newer one already settled.
        if (mySeq !== this._suggestSeq) return;
        this.setState({ suggestions: Array.isArray(res?.data) ? res.data : [], suggestBusy: false });
      } catch {
        if (mySeq !== this._suggestSeq) return;
        this.setState({ suggestions: [], suggestBusy: false });
      }
    }, 220);
  }

  /**
   * Fill the whole form from a picked suggestion. Every column the
   * suggest endpoint returns lands in form.* so the user can review +
   * edit before submitting; opens the details disclosure so the
   * pre-filled deeper fields are visible.
   */
  _pickSuggestion(idx) {
    const sug = this.state.suggestions || [];
    const row = sug[idx];
    if (!row) return;
    const cur = this.state.form;
    const merge = (key, val) => {
      if (val === null || val === undefined || val === '') return;
      // Don't clobber a value the user already typed by hand.
      if (cur[key] && cur[key] !== '') return;
      cur[key] = val;
    };
    cur.name = row.name || cur.name;
    merge('share', row.share);
    merge('entity_type', row.entity_type || 'individual');
    merge('email', row.email);
    merge('phone', row.phone);
    merge('billing_country', row.billing_country);
    merge('billing_postal_code', row.billing_postal_code);
    merge('billing_city', row.billing_city);
    merge('addr_street', row.addr_street);
    merge('addr_house_number', row.addr_house_number);
    merge('addr_building', row.addr_building);
    merge('addr_staircase', row.addr_staircase);
    merge('addr_floor', row.addr_floor);
    merge('addr_door', row.addr_door);
    merge('addr_hrsz', row.addr_hrsz);
    merge('birth_name', row.birth_name);
    merge('birth_date', row.birth_date);
    merge('birth_place', row.birth_place);
    merge('mothers_name', row.mothers_name);
    merge('id_card_number', row.id_card_number);
    merge('tax_id', row.tax_id);
    merge('company_reg', row.company_reg);
    merge('representative_name', row.representative_name);
    merge('representative_role', row.representative_role);
    merge('bank_account', row.bank_account);
    this.setState({ suggestions: null, detailsOpen: true });
  }

  async _load() {
    try {
      const res = await api.get(`/units/${this.unitId}/external-owners`);
      this.setState({ owners: res.data || [], error: null });
    } catch (err) {
      this.setState({ owners: [], error: err instanceof ApiError ? err.message : t('unit.external_owners.errors.load') });
    }
  }

  _openEdit(id) {
    const owner = (this.state.owners || []).find((o) => o.id === id);
    if (!owner) return;
    this.setState({
      editingId: id,
      addOpen: false,
      detailsOpen: true, // owner edits usually touch the detail fields
      error: null,
      suggestions: null,
      form: this._formFromOwner(owner),
    });
  }

  async _submit(e) {
    e.preventDefault();
    if (this.state.busy) return;
    const f = this.state.form;
    const name = (f.name || '').trim();
    if (name === '') {
      this.setState({ error: t('unit.external_owners.errors.name_required') });
      return;
    }
    // On edit we send every field (including empties) so clearing a
    // field actually nulls it server-side. On add we only ship
    // non-empty fields so the server writes NULL instead of empty
    // strings. entity_type is always sent — the server uses it to
    // decide which company-only columns to populate.
    const isEdit = !!this.state.editingId;
    const payload = { name, entity_type: f.entity_type || 'individual' };
    const carry = [
      'share', 'email', 'phone',
      'billing_country', 'billing_postal_code', 'billing_city',
      'addr_street', 'addr_house_number', 'addr_building',
      'addr_staircase', 'addr_floor', 'addr_door', 'addr_hrsz',
      'birth_name', 'birth_date', 'birth_place', 'mothers_name',
      'id_card_number', 'tax_id',
      'company_reg', 'representative_name', 'representative_role',
      'bank_account',
    ];
    for (const k of carry) {
      const v = (f[k] || '').trim ? f[k].trim() : f[k];
      if (isEdit) {
        payload[k] = v == null ? '' : v;
      } else if (v !== '' && v != null) {
        payload[k] = v;
      }
    }
    this.setState({ busy: true, error: null });
    try {
      if (isEdit) {
        const id = this.state.editingId;
        const row = await api.put(`/units/${this.unitId}/external-owners/${encodeURIComponent(id)}`, payload);
        this.setState({
          owners: (this.state.owners || []).map((o) => (o.id === id ? row : o)),
          editingId: null, detailsOpen: false, busy: false,
          form: this._blankForm(),
          suggestions: null,
        });
      } else {
        const row = await api.post(`/units/${this.unitId}/external-owners`, payload);
        this.setState({
          owners: [...(this.state.owners || []), row],
          addOpen: false, detailsOpen: false, busy: false,
          form: this._blankForm(),
          suggestions: null,
        });
      }
    } catch (err) {
      const fallback = this.state.editingId ? 'unit.external_owners.errors.update' : 'unit.external_owners.errors.add';
      this.setState({ busy: false, error: err instanceof ApiError ? err.message : (t(fallback) || t('unit.external_owners.errors.add')) });
    }
  }

  async _setIssuer(ownerId) {
    if (!this.propertyId || !ownerId) return;
    const previous = this.state.issuerId;
    // Optimistic update, rolled back if the server rejects (owner row no
    // longer belongs to a unit of this property, permission lost, etc.).
    this.setState({ issuerId: ownerId, error: null });
    try {
      await api.put(`/properties/${this.propertyId}/landlord`, { landlord_external_owner_id: ownerId });
      if (this._onIssuerChange) {
        try { this._onIssuerChange(ownerId); } catch {}
      }
    } catch (err) {
      this.setState({
        issuerId: previous,
        error: err instanceof ApiError ? err.message : t('unit.external_owners.errors.set_issuer'),
      });
    }
  }

  async _remove(id) {
    if (!id) return;
    if (!confirm(t('unit.external_owners.remove_confirm'))) return;
    try {
      await api.delete(`/units/${this.unitId}/external-owners/${encodeURIComponent(id)}`);
      this.setState({ owners: (this.state.owners || []).filter((o) => o.id !== id), error: null });
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('unit.external_owners.errors.remove') });
    }
  }
}

export function attachUnitExternalOwnersPanel(rootEl, props) {
  // jsdom (test env) doesn't ship CSS.escape; fall back to a literal
  // attribute-value escape that's enough for the UUID-shaped unit ids
  // we use.
  const esc = (typeof CSS !== 'undefined' && typeof CSS.escape === 'function')
    ? CSS.escape
    : (s) => String(s).replace(/["\\]/g, '\\$&');
  const slot = rootEl.querySelector(`[data-unit-external-owners-slot="${esc(props.unitId)}"]`);
  if (!slot) return null;
  const panel = new UnitExternalOwnersPanel(props);
  panel.render(slot);
  return panel;
}
